import type { Selection } from './editorState';

/** The verb a hint presses with: Click with a mouse, Tap on touch. */
export type SelectionHintVerb = 'Click' | 'Tap';

/** How to work a selected free element: a mouse turns it with the ↻ handle, touch (which has no ↻ handle) with a two-finger twist. */
const FREE_ELEMENT_HINTS: Record<SelectionHintVerb, string> = {
  Click: 'Drag middle to move, edges to resize, ◿ to add corners, and ↻ to rotate.',
  Tap: 'Drag middle to move, edges to resize, and ◿ to add corners. Twist two fingers to rotate.',
};

/** How a corner is cut, on the room, an alcove or an element. */
const CUT_CORNER = 'Drag ◿ to cut the corner.';

/** What the ⊕ menu adds to a wall, and to an alcove wall or a corner's cut, which take no alcove. */
const WALL_ITEMS = 'an alcove, door, opening, or window', ALCOVE_WALL_ITEMS = 'a door, opening, or window';

/**
 * The contextual how-to hint for `selection`, the one always-visible line in
 * the panel's footer on both platforms, pressing with `verb`.
 */
export function selectionHint(selection: Selection, verb: SelectionHintVerb): string {
  switch (selection.kind) {
    case 'edge':
      return `${verb} ⊕ to add ${WALL_ITEMS}.`;
    case 'alcoveWall':
      return `${verb} ⊕ to add ${ALCOVE_WALL_ITEMS}.`;
    case 'lineSeg':
      return 'Drag middle to move and ends to resize.';
    case 'inner':
      return FREE_ELEMENT_HINTS[verb];
    case 'corner':
    case 'alcoveCorner':
      return `${CUT_CORNER} On a cut corner, ${verb.toLowerCase()} ⊕ to add ${ALCOVE_WALL_ITEMS}.`;
    case 'innerCorner':
      return CUT_CORNER;
    case 'room':
      return `Drag edges to resize. ${verb} edges to select and add alcoves, doors, openings, or windows. Drag ◿ to add corners.`;
    default:
      return `${verb} floor, edge or object to select. Drag edges and objects to position.`;
  }
}
